import { useRef, useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { storeImage } from "../lib/sync";
import { fileToDataUri } from "../lib/image";
import { relative } from "../lib/format";
import type { Project } from "../lib/types";

/* Site photos pinned to a point on a drawing, so progress and defects are
   located in space rather than described in words. Click the sheet where the
   photo was taken; the pin lands there. */
export function PhotoPins({ p, canEdit, by, open, onClose }: { p: Project; canEdit: boolean; by: string; open: boolean; onClose: () => void }) {
  const [viewSheet, setViewSheet] = useState<string | null>(null);
  const pins = p.photoPins ?? [];
  const sheetsWithPins = [...new Set(pins.map((x) => x.sheetCode))];

  return (
    <>
      {sheetsWithPins.length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden", marginBottom: 22 }}>
          <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
            <span className="row" style={{ gap: 8 }}><Icon.pin width={15} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Photos on the drawings</span></span>
            <span className="dim" style={{ fontSize: 12 }}>{pins.length} on {sheetsWithPins.length} sheet{sheetsWithPins.length === 1 ? "" : "s"}</span>
          </div>
          <div className="row wrap" style={{ gap: 8, padding: "12px 18px" }}>
            {sheetsWithPins.map((code) => (
              <button key={code} className="chip" style={{ fontSize: 11.5 }} onClick={() => setViewSheet(code)}>
                {code} · {pins.filter((x) => x.sheetCode === code).length}
              </button>
            ))}
          </div>
        </div>
      )}
      {open && <PinModal p={p} by={by} onClose={onClose} />}
      {viewSheet && <SheetPinsModal p={p} code={viewSheet} canEdit={canEdit} onClose={() => setViewSheet(null)} />}
    </>
  );
}

function PinModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  const drawn = (p.deliverables ?? []).filter((d) => d.previewUrl && !d.archived);
  const [sheetId, setSheetId] = useState(drawn[0]?.id ?? "");
  const [photo, setPhoto] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [pt, setPt] = useState<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const sheet = drawn.find((d) => d.id === sheetId);

  async function pick(f: File | null) {
    if (!f) return;
    setBusy(true);
    try { setPhoto(await storeImage(await fileToDataUri(f), p.client?.code)); } catch { /* storage off */ }
    setBusy(false);
  }
  function place(e: React.MouseEvent<HTMLImageElement>) {
    const r = imgRef.current!.getBoundingClientRect();
    setPt({ x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
  }

  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Pin a photo to a drawing" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>Choose the sheet, add the photo, then click the drawing where it was taken.</p>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1, minWidth: 220 }}><label>Sheet</label>
            <select className="select" value={sheetId} onChange={(e) => { setSheetId(e.target.value); setPt(null); }} disabled={!drawn.length}>
              {drawn.length === 0 && <option>No drawn sheets</option>}
              {drawn.map((d) => <option key={d.id} value={d.id}>{d.code} — {d.title}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}><label>Photo</label>
            <label className="panel row between" style={{ padding: "9px 12px", borderStyle: "dashed", cursor: "pointer" }}>
              <span style={{ fontSize: 13 }}>{busy ? "Uploading…" : photo ? "Photo added" : "Choose a photo"}</span>
              <span className="btn soft sm">Browse</span>
              <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => pick(e.target.files?.[0] ?? null)} />
            </label>
          </div>
        </div>
        {sheet?.previewUrl && (
          <div style={{ position: "relative", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
            <img ref={imgRef} src={sheet.previewUrl} alt={sheet.title} onClick={place} style={{ width: "100%", display: "block", cursor: "crosshair", maxHeight: 420, objectFit: "contain" }} />
            {pt && <span style={{ position: "absolute", left: `${pt.x * 100}%`, top: `${pt.y * 100}%`, transform: "translate(-50%,-100%)", color: "var(--danger)" }}><Icon.pin width={26} /></span>}
          </div>
        )}
        <div className="field"><label>Note <span className="dim">(optional)</span></label><input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What the photo shows." /></div>
        <div className="row between">
          <span className="dim" style={{ fontSize: 12 }}>{pt ? "Location set — click again to move it." : "Click the drawing to place the pin."}</span>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
            <button className="btn accent" disabled={busy || !photo || !pt || !sheet} onClick={() => { actions.addPhotoPin(p.id, { sheetCode: sheet!.code, building: sheet!.building, x: pt!.x, y: pt!.y, url: photo!, note, by }); onClose(); }}><Icon.check width={15} />Pin photo</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function SheetPinsModal({ p, code, canEdit, onClose }: { p: Project; code: string; canEdit: boolean; onClose: () => void }) {
  const sheet = (p.deliverables ?? []).find((d) => d.code === code && d.previewUrl);
  const pins = (p.photoPins ?? []).filter((x) => x.sheetCode === code);
  const [openPin, setOpenPin] = useState<string | null>(null);
  const active = pins.find((x) => x.id === openPin);
  return (
    <Modal open onClose={onClose} title={`${code} — pinned photos`} wide>
      <div className="col" style={{ gap: 14 }}>
        {sheet?.previewUrl && (
          <div style={{ position: "relative", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
            <img src={sheet.previewUrl} alt={code} style={{ width: "100%", display: "block", maxHeight: 460, objectFit: "contain" }} />
            {pins.map((x, i) => (
              <button key={x.id} title={x.note || `Photo ${i + 1}`} onClick={() => setOpenPin(openPin === x.id ? null : x.id)}
                style={{ position: "absolute", left: `${x.x * 100}%`, top: `${x.y * 100}%`, transform: "translate(-50%,-100%)", background: "none", border: "none", cursor: "pointer", color: openPin === x.id ? "var(--accent-ink)" : "var(--danger)", padding: 0 }}>
                <Icon.pin width={26} />
              </button>
            ))}
          </div>
        )}
        {active && (
          <div className="panel" style={{ padding: 12 }}>
            <img src={active.url} alt="" style={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 8, background: "#fff" }} />
            <div className="row between wrap" style={{ gap: 10, marginTop: 8 }}>
              <span style={{ fontSize: 13 }}>{active.note || "No note"}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>{active.by} · {relative(active.on)}</span>
            </div>
            {canEdit && <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={() => { if (confirm("Remove this pin?")) { actions.removePhotoPin(p.id, active.id); setOpenPin(null); } }}><Icon.close width={13} />Remove pin</button>}
          </div>
        )}
        {!active && <div className="dim" style={{ fontSize: 12.5, textAlign: "center" }}>Click a pin to see its photo.</div>}
      </div>
    </Modal>
  );
}
